import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CommerceCatalogWorkspace,
  HorizontalStack,
  PropertyGrid,
  PropertyItem,
  RegistryStatus,
  toast,
  Surface,
  VerticalStack,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  Checkbox,
  DescriptionText,
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';
import { BadgePercent, KeyRound, Pencil, Trash2 } from 'lucide-react';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const DISCOUNT_SORT_FIELDS = ['benefit', 'created_at', 'ends_at', 'name', 'priority', 'scope', 'status', 'updated_at'] as const;
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import {
  createPromotion,
  deletePromotion,
  type Catalog,
  type CatalogPublication,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  type Market,
  type MarketGroup,
  listPromotions,
  type Product,
  type Promotion,
  updatePromotion,
} from '@/lib/commerce';
import {
  buildPromotionForm as buildEditorForm,
  buildPromotionPayload as buildEditorPayload,
  createEmptyPromotionForm,
  type DiscountFormState,
} from '@/features/commerce-admin/promotionEditorContract';
import { formatMinorCurrency } from '@/features/commerce-admin/currency';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
} from '@/views/commerce/catalogHierarchyShared';
import {
  buildCatalogSelectorOptions,
  buildMarketSelectorOptions,
  buildObservedScopeOptions,
  buildProductSelectorOptions,
  buildPublicationSelectorOptions,
  buildMarketGroupSelectorOptions,
  buildServiceClassSelectorOptions,
  buildServiceGroupSelectorOptions,
  buildTenantSelectorOptions,
  buildVariantSelectorOptions,
  loadCommerceSelectorRecords,
} from '@/views/commerce/selectorOptions';
import { PromotionWizard } from './PromotionWizard';
import { PromotionCodesPanel, PromotionCodeEditorWizard } from './PromotionCodesPanel';

function createEmptyForm(): DiscountFormState {
  return createEmptyPromotionForm();
}

function buildFormFromPromotion(promotion: Promotion): DiscountFormState {
  return buildEditorForm(promotion);
}

function buildPromotionPayload(form: DiscountFormState, includeCodes: boolean): Record<string, unknown> {
  return buildEditorPayload(form, includeCodes);
}

function buildBenefitSummary(promotion: Promotion): string {
  const benefit = promotion.benefits?.[0];
  if (!benefit) {
    return 'No benefit configured';
  }
  switch (benefit.benefit_type) {
    case 'amount_off':
      return `${formatMinorCurrency(benefit.amount_minor ?? 0, benefit.currency || 'USD')} off${benefit.discount_length ? ` for ${benefit.discount_length} periods` : ''}`;
    case 'percent_off':
      return `${benefit.percentage ?? 0}% off${benefit.discount_length ? ` for ${benefit.discount_length} periods` : ''}`;
    case 'fixed_price':
      return `Fixed price ${formatMinorCurrency(benefit.amount_minor ?? 0, benefit.currency || 'USD')}${benefit.discount_length ? ` for ${benefit.discount_length} periods` : ''}`;
    case 'fee_waiver':
      return 'Fee waiver';
    case 'free_periods':
      return `${benefit.discount_length} free billing cycles${benefit.application_policy === 'extend_free_periods' ? ' · extends free periods' : ''}`;
    default:
      return benefit.benefit_type;
  }
}

function buildEligibilitySummary(promotion: Promotion): string {
  const scopes = promotion.eligibility_scopes ?? [];
  if (!scopes.length) {
    return 'Tenant-wide eligibility';
  }
  const scope = scopes[0];
  return `${scope.scope_type}: ${scope.scope_value || 'unspecified'}${scope.exclude ? ' (exclude)' : ''}${scopes.length > 1 ? ` +${scopes.length - 1}` : ''}`;
}

function buildExpirationSummary(promotion: Promotion): string {
  if (!promotion.ends_at) return 'No expiration';
  const expiration = new Date(promotion.ends_at);
  return Number.isNaN(expiration.getTime()) ? 'Invalid expiration' : expiration.toLocaleString();
}

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
    case 'published':
      return 'success';
    case 'draft':
      return 'warning';
    case 'archived':
    case 'inactive':
      return 'muted';
    default:
      return 'default';
  }
}

export default function DiscountsPage() {
  const { activeTenant, organizations } = useScope();
  const navigation = useNavigation();
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [publications, setPublications] = useState<CatalogPublication[]>([]);
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [groups, setGroups] = useState<MarketGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectorLoading, setSelectorLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const deletePending = useRef(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [codesOpen, setCodesOpen] = useState(false);
  const [codeEditorOpen, setCodeEditorOpen] = useState(false);
  const [codesPromotion, setCodesPromotion] = useState<Promotion | null>(null);
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: ['active', 'archived', 'draft'] },
    sortFields: DISCOUNT_SORT_FIELDS,
  });
  const searchQuery = query.search;
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const [totalPromotions, setTotalPromotions] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const requestSequence = useRef(0);
  const [form, setForm] = useState<DiscountFormState>(createEmptyForm());

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const columnFilters = useMemo<ColumnFiltersState>(() => (
    query.facets.status.length ? [{ id: 'status', value: query.facets.status }] : []
  ), [query.facets.status]);

  const loadData = async (authoritativePromotion?: Promotion) => {
    const requestId = ++requestSequence.current;
    if (!activeTenant?.id) {
      return;
    }
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const data = await listPromotions({
        search: searchQuery || undefined,
        status: query.facets.status,
        sort: query.sort,
        include_facets: ['status'],
        ...toLimitOffset(query.pagination),
      });
      if (requestId !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(data.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setPromotions(data.items.map(promotion => (
        promotion.promotion_id === authoritativePromotion?.promotion_id
          ? authoritativePromotion
          : promotion
      )));
      setTotalPromotions(data.total_count);
      setStatusCounts(Object.fromEntries((data.facets.status ?? []).map(bucket => [bucket.value, bucket.count])));
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load promotions.'));
      }
      if (requestId === requestSequence.current && !authoritativePromotion) setPromotions([]);
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeTenant?.id) {
      setPromotions([]);
      setTotalPromotions(0);
      setStatusCounts({});
      setLoading(false);
      return;
    }
    void loadData();
  }, [activeTenant?.id, query.facets.status, query.pagination, query.sort, searchQuery]);

  useEffect(() => {
    const tenantId = activeTenant?.id;
    if (!tenantId) {
      setPublications([]);
      setCatalogs([]);
      setProducts([]);
      setMarkets([]);
      setGroups([]);
      return;
    }

    let cancelled = false;

    const loadSelectors = async () => {
      setSelectorLoading(true);
      try {
        const data = await loadCommerceSelectorRecords(tenantId);
        if (cancelled) {
          return;
        }
        setPublications(data.publications || []);
        setCatalogs(data.catalogs || []);
        setProducts(data.products || []);
        setMarkets(data.markets || []);
        setGroups(data.groups || []);
      } catch (err) {
        if (!cancelled) {
          setError(current => current || getCommerceErrorMessage(err, 'Failed to load discount target options.'));
        }
      } finally {
        if (!cancelled) {
          setSelectorLoading(false);
        }
      }
    };

    void loadSelectors();

    return () => {
      cancelled = true;
    };
  }, [activeTenant?.id]);

  const catalogsById = useMemo(() => {
    const map = new Map<string, Catalog>();
    catalogs.forEach(catalog => {
      map.set(catalog.catalog_id, catalog);
    });
    return map;
  }, [catalogs]);

  const publicationOptions = useMemo(() => buildPublicationSelectorOptions(publications), [publications]);
  const catalogOptions = useMemo(() => buildCatalogSelectorOptions(catalogs), [catalogs]);
  const productOptions = useMemo(() => buildProductSelectorOptions(products, catalogsById), [products, catalogsById]);
  const variantOptions = useMemo(() => buildVariantSelectorOptions(products, catalogsById), [products, catalogsById]);
  const marketOptions = useMemo(() => buildMarketSelectorOptions(markets), [markets]);
  const segmentOptions = useMemo(() => buildMarketGroupSelectorOptions(groups), [groups]);
  const sellingTenantOptions = useMemo(
    () =>
      buildTenantSelectorOptions({
        activeTenant,
        organizations,
        observedTenantIds: publications.flatMap(publication => publication.selling_tenant_ids || []),
      }),
    [activeTenant, organizations, publications],
  );
  const storeOptions = useMemo(() => buildObservedScopeOptions(publications, 'stores'), [publications]);
  const serviceClassOptions = useMemo(() => buildServiceClassSelectorOptions(products), [products]);
  const serviceGroupOptions = useMemo(() => buildServiceGroupSelectorOptions(products), [products]);

  const eligibilityValueConfigs = useMemo(() => {
    const configFor = (scopeType: string) => {
      switch (scopeType) {
      case 'selling_tenant':
        return {
          options: sellingTenantOptions,
          placeholder: 'Choose selling tenant',
          emptyMessage: 'No selling tenants are available yet.',
        };
      case 'publication':
        return {
          options: publicationOptions,
          placeholder: 'Choose publication',
          emptyMessage: 'No publications are available yet.',
        };
      case 'catalog':
        return {
          options: catalogOptions,
          placeholder: 'Choose catalog',
          emptyMessage: 'No catalogs are available yet.',
        };
      case 'product':
        return {
          options: productOptions,
          placeholder: 'Choose product',
          emptyMessage: 'No products are available yet.',
        };
      case 'variant':
        return {
          options: variantOptions,
          placeholder: 'Choose variant',
          searchPlaceholder: 'Search variants by name, key, product, or ID...',
          emptyMessage: 'No variants are available yet.',
        };
      case 'market':
        return {
          options: marketOptions,
          placeholder: 'Choose market',
          emptyMessage: 'No markets are available yet.',
        };
      case 'market_segment':
        return {
          options: segmentOptions,
          placeholder: 'Choose market segment',
          emptyMessage: 'No market groups are available yet.',
        };
      case 'service_class':
        return {
          options: serviceClassOptions,
          placeholder: 'Choose service class',
          emptyMessage: 'No service classes are available yet.',
        };
      case 'service_group':
        return {
          options: serviceGroupOptions,
          placeholder: 'Choose service group',
          emptyMessage: 'No service groups are available yet.',
        };
      case 'store':
        return {
          options: storeOptions,
          placeholder: 'Choose store',
          emptyMessage: 'No stores are available yet.',
        };
      default:
        return {
          options: [],
          placeholder: 'Choose scope value',
          emptyMessage: 'No scope values are available yet.',
        };
      }
    };
    return Object.fromEntries([
      'selling_tenant',
      'publication',
      'catalog',
      'product',
      'variant',
      'market',
      'market_segment',
      'service_class',
      'service_group',
      'store',
    ].map(scopeType => [scopeType, configFor(scopeType)]));
  }, [
    catalogOptions,
    marketOptions,
    productOptions,
    publicationOptions,
    segmentOptions,
    sellingTenantOptions,
    serviceClassOptions,
    serviceGroupOptions,
    storeOptions,
    variantOptions,
  ]);

  const handleEdit = useCallback((promotion: Promotion) => {
    setEditingId(promotion.promotion_id);
    setForm(buildFormFromPromotion(promotion));
    setEditorOpen(true);
  }, []);

  const handleSave = async (wizardForm: DiscountFormState) => {
    setError(null);
    try {
      const payload = buildPromotionPayload(wizardForm, !editingId);
      if (editingId) {
        const updatedPromotion = await updatePromotion(editingId, payload);
        setPromotions(current => current.map(promotion => (
          promotion.promotion_id === updatedPromotion.promotion_id
            ? updatedPromotion
            : promotion
        )));
        await loadData(updatedPromotion);
      } else {
        await createPromotion(payload);
        await loadData();
      }
      setEditorOpen(false);
    } catch (err) {
      setError(getCommerceErrorMessage(err, 'Failed to save promotion.'));
      throw err;
    }
  };

  const handleDelete = async (promotion: Promotion) => {
    if (!activeTenant?.id || deletePending.current) return;
    if (!window.confirm(`Delete discount "${promotion.name}"? It will no longer apply to new purchases. Existing orders and redemption history will be kept.`)) return;
    deletePending.current = true;
    setDeletingId(promotion.promotion_id);
    setError(null);
    try {
      await deletePromotion(promotion.promotion_id);
      ++requestSequence.current;
      setPromotions(current => current.filter(item => item.promotion_id !== promotion.promotion_id));
      setRowSelection({});
      toast.success('Discount deleted', { description: promotion.name });
      await loadData();
    } catch (err) {
      const message = getCommerceErrorMessage(err, 'Failed to delete discount.');
      setError(message);
      toast.error(message);
    } finally {
      deletePending.current = false;
      setDeletingId(null);
    }
  };

  const openCodes = useCallback((promotion: Promotion) => {
    setCodesPromotion(promotion);
    setCodeEditorOpen(false);
    setCodesOpen(true);
  }, []);

  const closeCodes = useCallback(() => {
    setCodesOpen(false);
    setCodeEditorOpen(false);
    setCodesPromotion(null);
  }, []);

  const openCodeEditor = useCallback(() => {
    setCodeEditorOpen(true);
  }, []);

  const closeCodeEditor = useCallback(() => {
    setCodeEditorOpen(false);
  }, []);

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'discounts',
    searchQuery,
    searchPlaceholder: 'Search discounts...',
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to manage discounts.',
    onSelectSection: sectionId => navigation.navigate(buildCommerceSectionRoute(sectionId)),
    primaryAction: {
      id: 'create-promotion',
      label: 'New Discount',
      onAction: () => {
        setEditingId(null);
        setForm(createEmptyForm());
        setEditorOpen(true);
      },
    },
  });

  const columns = useMemo<ColumnDef<Promotion>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'name',
      accessorKey: 'name',
      size: 300,
      header: 'Discount / Promotion',
      cell: ({ row }) => {
        const promotion = row.original;
        return (
          <button
            type="button"
            onClick={() => handleEdit(promotion)}
            className="group min-w-0 text-left"
            aria-label={`Edit ${promotion.name}`}
          >
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {promotion.name}
            </p>
            <DescriptionText className="line-clamp-1 text-[11px]">
              {promotion.promotion_key} • {promotion.discount_type}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'status',
      size: 100,
      header: 'Status',
      cell: ({ row }) => (
        <RegistryStatus label={row.original.status} variant={statusVariant(row.original.status)} />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: 'benefit',
      accessorFn: promotion => buildBenefitSummary(promotion),
      header: 'Benefit',
      size: 200,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{buildBenefitSummary(row.original)}</span>
      ),
    },
    {
      id: 'scope',
      accessorFn: promotion => buildEligibilitySummary(promotion),
      header: 'Eligibility',
      size: 250,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{buildEligibilitySummary(row.original)}</span>
      ),
    },
    {
      id: 'ends_at',
      accessorFn: promotion => promotion.ends_at || '',
      header: 'Expires',
      size: 180,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{buildExpirationSummary(row.original)}</span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 100,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => openCodes(row.original)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            aria-label={`Codes for ${row.original.name}`}
          >
            <KeyRound className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => handleEdit(row.original)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            aria-label={`Modify ${row.original.name}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => void handleDelete(row.original)}
            disabled={deletingId !== null}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-destructive/40 hover:text-destructive hover:bg-destructive/5 disabled:opacity-50"
            aria-label={`Delete ${row.original.name}`}
            title={deletingId === row.original.promotion_id ? 'Deleting discount...' : 'Delete discount'}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ], [handleEdit, openCodes, handleDelete, deletingId]);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: DISCOUNT_SORT_FIELDS, registry: 'discounts' });

  let takeoverContent: React.ReactNode;
  if (codesOpen && codesPromotion) {
    if (codeEditorOpen) {
      takeoverContent = (
        <PromotionCodeEditorWizard
          isOpen={true}
          onClose={closeCodeEditor}
          promotion={codesPromotion}
          onSaved={closeCodeEditor}
        />
      );
    } else {
      takeoverContent = (
        <PromotionCodesPanel
          promotion={codesPromotion}
          onBack={closeCodes}
          onAddCode={openCodeEditor}
        />
      );
    }
  } else {
    takeoverContent = (
      <PromotionWizard
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
        initialForm={form}
        isEditing={Boolean(editingId)}
        eligibilityValueConfigs={eligibilityValueConfigs}
        selectorLoading={selectorLoading}
      />
    );
  }

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      padded={false}
      takeoverActive={editorOpen || codesOpen}
      replaceShellHeaderWhenTakeoverActive
      takeoverContent={takeoverContent}
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Discounts' }]}
          title="Discounts"
          description="Manage discount types, qualification conditions, eligibility, and commercial benefits."
        />
      }
    >
      <RegistryWorkspace>
        <RegistryTableSurface>
          <DataTable
            mode="manual"
            columns={columns}
            data={promotions}
            sorting={sorting}
            onSortingChange={updater => {
              const next = typeof updater === 'function' ? updater(sorting) : updater;
              query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
            }}
            globalFilter={searchQuery}
            columnFilters={columnFilters}
            onColumnFiltersChange={updater => {
              const next = typeof updater === 'function' ? updater(columnFilters) : updater;
              const filter = next.find(item => item.id === 'status');
              query.setFacet('status', Array.isArray(filter?.value) ? filter.value.map(String) : []);
            }}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={query.pagination}
            onPaginationChange={query.onPaginationChange}
            manualPagination
            totalRowCount={totalPromotions}
            loading={loading}
            className="border-none rounded-none"
            paginationClassName="px-0 py-0"
            showPagination
            emptyState={
              <div className="px-0 py-12 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted/30">
                  <BadgePercent className="h-6 w-6 text-muted-foreground/60" />
                </div>
                <p className="text-sm font-black text-foreground">No Discounts or Promotions</p>
                <DescriptionText className="mt-1 text-xs">
                  {searchQuery || query.facets.status.length
                    ? 'No discounts or promotions matched the current search or filters.'
                    : 'Create discount campaigns and promotional codes for your catalog.'}
                </DescriptionText>
                {searchQuery || query.facets.status.length ? (
                  <button type="button" onClick={query.clear} className="mt-3 text-xs font-semibold text-primary hover:underline">
                    Reset search and filters
                  </button>
                ) : null}
              </div>
            }
            renderToolbar={table => (
              <DataTableToolbar
                table={table}
                searchValue={searchQuery}
                onSearchChange={query.setSearch}
                searchPlaceholder="Search all discounts..."
                resultCount={totalPromotions}
                className="border-b border-border/40 bg-transparent px-0 py-0"
                filters={
                  <DataTableFacetedFilter
                    title="Status"
                    selectedValues={new Set(query.facets.status)}
                    onFilterChange={values => query.setFacet('status', Array.from(values))}
                    counts={statusCounts}
                    options={['active', 'draft', 'archived'].map(value => ({
                      label: value.charAt(0).toUpperCase() + value.slice(1),
                      value,
                    }))}
                  />
                }
              />
            )}
          />
        </RegistryTableSurface>
      </RegistryWorkspace>
    </CommerceCatalogWorkspace>
  );
}
